import { EnvironmentProviders, inject, provideEnvironmentInitializer } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CanMatchFn, NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { AppDatabase } from '../database/app.database';
import { LocalStore } from '../database/config/database-config-token';

/**
 * Selects the local store of a route area. A `canMatch` guard runs before the lazy load, the
 * other guards, and the resolvers, so all of them read and write the correct database.
 */
export function selectLocalStore(store: LocalStore): CanMatchFn {
	return () => {
		inject(AppDatabase).use(store);
		return true;
	};
}

/**
 * Selects the store of the page on screen again when the navigation does not complete.
 * A `selectLocalStore` guard can run for a navigation that the router then cancels, and
 * without this the page on screen would continue with the store of the other area.
 */
export function provideLocalStoreRouting(): EnvironmentProviders {
	return provideEnvironmentInitializer(() => {
		const database = inject(AppDatabase);
		let committedStore = database.store();

		inject(Router).events
			.pipe(takeUntilDestroyed())
			.subscribe(event => {
				if (event instanceof NavigationEnd) {
					committedStore = database.store();
				} else if (event instanceof NavigationCancel || event instanceof NavigationError) {
					database.use(committedStore);
				}
			});
	});
}
